// Harmony compatibility shim.
//
// The Bliss tool components (Image Studio, Lead Enrichment, etc.) call
// `window.harmony.api.getUserName()` to derive the logged-in alias. They
// retry briefly because in real Harmony the global isn't always present on
// first paint. We replicate the API surface so those files run unchanged.
//
// Two functions:
//   - installHarmonyShim(): set up the global with a placeholder username so
//     getUserName() never returns undefined. Must run BEFORE React renders
//     anything (so call from main.jsx, not a useEffect).
//   - setHarmonyUsername(name): update the username once auth resolves. The
//     AuthProvider calls this after login completes.
//
// Why a global instead of refactoring every callsite to useAuth()?
//   - 10+ files use getUserName() with retry loops. Each refactor is a chance
//     to break something subtle (e.g. order of state setters, race
//     conditions during the brief "username = unknown" window).
//   - The shim is mechanical — same API, same retry behavior — so the tool
//     code reads identically to its Harmony version.
//   - Once the migration ships and stabilises, we can replace callsites with
//     useAuth() in a follow-up PR. Not during the migration.

let cachedUsername = 'unknown';

export function installHarmonyShim() {
  if (typeof window === 'undefined') return;
  // Don't clobber a pre-existing real Harmony global (if anyone ever runs
  // this code inside actual Harmony for some reason).
  if (window.harmony?.api?.getUserName) return;

  window.harmony = window.harmony || {};
  window.harmony.api = window.harmony.api || {};
  window.harmony.api.getUserName = () => cachedUsername;
}

export function setHarmonyUsername(name) {
  if (!name) return;
  cachedUsername = name;
  // Some tool components also poll `getUserName()` synchronously inside
  // useEffect — they'll pick up the new value on their next call.
}

export function getHarmonyUsername() {
  return cachedUsername;
}

// Email -> alias. Cognito Google federation gives us full emails like
// `rohitrjn@amazon.com`. The existing run-ID format is `rohitrjn_<timestamp>`,
// not the full email, so we strip the domain. Defined once here, used
// everywhere — never re-derive at the callsite.
export function deriveUsernameFromEmail(email) {
  if (!email) return 'unknown';
  const at = email.indexOf('@');
  if (at <= 0) return email; // no @ — already a bare username, unusual but ok
  return email.slice(0, at);
}
